import { Fragment, type ReactNode } from 'react'
import { cn } from '../lib/cn.ts'
import { parseMarkdown } from './parse.ts'
import type { Block, Inline, ListItem } from './types.ts'

/**
 * Renders the block tree `parse.ts` produces; every ambiguity was settled there.
 *
 * There is no `dangerouslySetInnerHTML` here and there never should be: the text
 * comes from an external system, so the guarantee that it cannot introduce
 * markup holds structurally rather than by sanitising.
 */
export interface MarkdownProps {
  children: string
  className?: string
}

export function Markdown({ children, className }: MarkdownProps) {
  const blocks = parseMarkdown(children)
  if (blocks.length === 0) return null

  return (
    <div className={cn('flex flex-col gap-3 text-sm text-foreground', className)}>
      {blocks.map((block, i) => (
        <BlockView key={i} block={block} />
      ))}
    </div>
  )
}

const HEADING_SIZE = [
  'text-base font-semibold',
  'text-base font-semibold',
  'text-sm font-semibold',
  'text-sm font-semibold',
  'text-[13px] font-semibold',
  'text-[13px] font-semibold',
]

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'heading': {
      const Tag = `h${block.level}` as 'h1'
      return (
        <Tag
          className={cn(
            'mt-1 text-foreground first:mt-0',
            HEADING_SIZE[block.level - 1] ?? HEADING_SIZE[5],
          )}
        >
          <InlineView nodes={block.content} />
        </Tag>
      )
    }

    case 'paragraph':
      return (
        <p className="leading-relaxed break-words text-foreground/90">
          <InlineView nodes={block.content} />
        </p>
      )

    case 'code':
      // Its own scroll container: a wide line has to move inside the block
      // rather than widening the panel it sits in.
      return (
        <pre className="overflow-x-auto rounded-lg border border-border/60 bg-background/60 p-3">
          <code className="font-mono text-[12.5px] leading-relaxed text-foreground/90">
            {block.value}
          </code>
        </pre>
      )

    case 'quote':
      return (
        <blockquote className="flex flex-col gap-2 border-l-2 border-border pl-3 text-foreground/70">
          {block.blocks.map((inner, i) => (
            <BlockView key={i} block={inner} />
          ))}
        </blockquote>
      )

    case 'rule':
      return <hr className="border-border/60" />

    case 'list': {
      const List = block.ordered ? 'ol' : 'ul'
      return (
        <List className="flex flex-col gap-1.5">
          {block.items.map((item, i) => (
            <ItemView key={i} item={item} ordered={block.ordered} index={i} />
          ))}
        </List>
      )
    }
  }
}

function ItemView({ item, ordered, index }: { item: ListItem; ordered: boolean; index: number }) {
  return (
    <li
      className="flex gap-2 leading-relaxed text-foreground/90"
      style={{ paddingLeft: `${item.depth * 1.1}rem` }}
    >
      <Marker checked={item.checked} ordered={ordered} index={index} />
      <span className="min-w-0 break-words">
        <InlineView nodes={item.content} />
      </span>
    </li>
  )
}

function Marker({
  checked,
  ordered,
  index,
}: {
  checked: boolean | null
  ordered: boolean
  index: number
}) {
  if (checked !== null) {
    return (
      <span
        aria-hidden
        className={cn(
          'mt-[3px] flex size-3.5 shrink-0 items-center justify-center rounded-[4px] border',
          checked ? 'border-primary bg-primary text-primary-foreground' : 'border-border',
        )}
      >
        {checked && (
          <svg viewBox="0 0 12 12" className="size-2.5" fill="none" stroke="currentColor">
            <path d="M2.5 6.2 4.8 8.5 9.5 3.8" strokeWidth="2" strokeLinecap="round" />
          </svg>
        )}
      </span>
    )
  }

  return (
    <span aria-hidden className="shrink-0 text-muted tabular-nums">
      {ordered ? `${index + 1}.` : '•'}
    </span>
  )
}

function InlineView({ nodes }: { nodes: Inline[] }): ReactNode {
  return nodes.map((node, i) => {
    switch (node.kind) {
      case 'text':
        return <Fragment key={i}>{node.value}</Fragment>

      case 'code':
        return (
          <code
            key={i}
            className="rounded bg-background/70 px-1 py-0.5 font-mono text-[12.5px] text-foreground"
          >
            {node.value}
          </code>
        )

      case 'strong':
        return (
          <strong key={i} className="font-semibold text-foreground">
            <InlineView nodes={node.children} />
          </strong>
        )

      case 'em':
        return (
          <em key={i} className="italic">
            <InlineView nodes={node.children} />
          </em>
        )

      case 'link':
        return (
          <a
            key={i}
            href={node.href}
            target="_blank"
            rel="noreferrer noopener"
            className="text-primary underline decoration-primary/40 underline-offset-2 hover:decoration-primary"
          >
            <InlineView nodes={node.children} />
          </a>
        )
    }
  })
}
